import React, {useEffect, useMemo, useState} from 'react';
import {collection, deleteDoc, doc, getDoc, getDocs, updateDoc} from "firebase/firestore";
import {Button, Select} from "@headlessui/react";
import {
    ArrowPathIcon, ArrowUpIcon, DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import {db} from "../firebase/firebase";
import {useAuth} from "../contexts/authContext";
import {useDarkMode} from "../contexts/darkModeContext";
import Loading from "./Widgets/Loading";
import OrderSection from "./Orders/OrderSection";

import {classNames, downloadExcel, getQuarterString, numberWithCommas} from "./GLOBAL/GlobalFunctions";
import NewOrder from "./OrderForm/NewOrder";
import OrderSearchInput from "./Orders/OrderSearchInput";
import {useNotification} from "./Notifications/NotificationProvider";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {countUniqueCustomerCodes} from "./GLOBAL/OrderFunctions";
import ActionPillButton from "./Widgets/ActionPillButton";
import {getStoredJSON, setStoredJSON} from "./GLOBAL/storageUtils";
import {mapWithConcurrency} from "./GLOBAL/promiseUtils";
import CustomButton from "./Widgets/CustomButton";


const Orders = () => {
    const { currentUser } = useAuth();
    
    const [orderForm, setOrderForm] = useState(false);
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });

    const [status, setStatus] = useState("A");

    const { darkMode } = useDarkMode();
    const { triggerNotification } = useNotification();
    
    const [orders, setOrders] = useState([]);
    const [ordersPending, setOrdersPending] = useState([]);
    const [ordersPlanned, setOrdersPlanned] = useState([]);

    const [filteredOrders, setFilteredOrders] = useState([]);
    const [filteredPendingOrders, setFilteredPendingOrders] = useState([]);
    const [filteredOrdersPlanned, setFilteredOrdersPlanned] = useState([]);

    const [searchQuery, setSearchQuery] = useState("");

    const [AscDesc, setAscDesc] = useState(true);
    const [DateAscDesc, setDateAscDesc] = useState(true);
    
    const [fname, setFname] = useState("");

    const orderCacheOptions = useMemo(() => ({
        maxBytes: 900000,
        evictPrefixes: [
            `plannedOrders_${currentUser?.uid || ""}`,
            `pendingOrders_${currentUser?.uid || ""}`,
            `activeOrders_${currentUser?.uid || ""}`,
            `customerAddresses_${currentUser?.uid || ""}`,
        ],
    }), [currentUser?.uid]);

    const toggleAscDesc = () => {
        // Toggle AscDesc and sort the orders array
        setAscDesc((prev) => !prev);

        if(status === "A"){
            const sortedOrders = [...filteredOrders].sort((a, b) => {
                return AscDesc
                    ? a.grandTotal - b.grandTotal  // Ascending
                    : b.grandTotal - a.grandTotal; // Descending
            });
            setFilteredOrders(sortedOrders);        }
        if (status === "P"){
            const sortedOrders = [...filteredPendingOrders].sort((a, b) => {
                return AscDesc
                    ? a.grandTotal - b.grandTotal  // Ascending
                    : b.grandTotal - a.grandTotal; // Descending
            });
            setFilteredPendingOrders(sortedOrders);        }
        if (status === "Planned"){
            const sortedOrders = [...filteredOrdersPlanned].sort((a, b) => {
                return AscDesc
                    ? a.grandTotal - b.grandTotal  // Ascending
                    : b.grandTotal - a.grandTotal; // Descending
            });
            setFilteredOrdersPlanned(sortedOrders);        }
    };

    const toggleDateAscDesc = () => {
        // Toggle dateAscDesc and sort the orders array
        setDateAscDesc((prev) => !prev);

        if (status === "A") {
            const sortedOrders = [...filteredOrders].sort((a, b) => {
                return DateAscDesc
                    ? new Date(a.orderDate) - new Date(b.orderDate)  // Ascending
                    : new Date(b.orderDate) - new Date(a.orderDate); // Descending
            });
            setFilteredOrders(sortedOrders);
        }
        if (status === "P") {
            const sortedOrders = [...filteredPendingOrders].sort((a, b) => {
                return DateAscDesc
                    ? new Date(a.orderDate) - new Date(b.orderDate)  // Ascending
                    : new Date(b.orderDate) - new Date(a.orderDate); // Descending
            });
            setFilteredPendingOrders(sortedOrders);
        }
        if (status === "Planned") {
            const sortedOrders = [...filteredOrdersPlanned].sort((a, b) => {
                return DateAscDesc
                    ? new Date(a.orderDate) - new Date(b.orderDate)  // Ascending
                    : new Date(b.orderDate) - new Date(a.orderDate); // Descending
            });
            setFilteredOrdersPlanned(sortedOrders);
        }
    };
    

    useEffect(() => {
        
        const fetchOrdersPending = async () => {
            if (currentUser) {
                try {
                    const storedPendingData = getStoredJSON(`pendingOrders_${currentUser.uid}`);
                    if (storedPendingData) {
                        setOrdersPending(storedPendingData);
                        setFilteredPendingOrders(storedPendingData);
                    } else {

                        const clientsCollectionRef = collection(db, `Customers/${currentUser.uid}/clients`);
                        const clientsSnapshot = await getDocs(clientsCollectionRef);
                        let allPendingData = [];

                        for (const clientDoc of clientsSnapshot.docs) {
                            const clientId = clientDoc.id;

                            const pendingCollectionRef = collection(db, `Customers/${currentUser.uid}/clients/${clientId}/pending`);
                            const pendingSnapshot = await getDocs(pendingCollectionRef);

                            pendingSnapshot.forEach((pendingDoc) => {
                                allPendingData.push({
                                    clientId,
                                    id: pendingDoc.id, // Add the document ID
                                    ...pendingDoc.data(),
                                });
                            });
                        }

                        if (allPendingData.length > 0) {
                            setOrdersPending(allPendingData);
                            setFilteredPendingOrders(allPendingData);
                            setStoredJSON(`pendingOrders_${currentUser.uid}`, allPendingData, orderCacheOptions);
                        } else {
                            setOrdersPending([]);
                            setFilteredPendingOrders([]);
                            console.log('Pending Order document not found');
                        }

                    }
                } catch (error) {
                    console.error('Error fetching pending orders data:', error);
                }
            }
        };

        const fetchOrdersPlanned = async () => {
            if (currentUser) {
                try {
                    // Check if data is stored in local storage
                    const storedPlannedData = getStoredJSON(`plannedOrders_${currentUser.uid}`);
                    if (storedPlannedData) {
                        setOrdersPlanned(storedPlannedData);
                        setFilteredOrdersPlanned(storedPlannedData);
                    } else {
                        // Reference to the clients collection
                        const clientsCollectionRef = collection(db, `Customers/${currentUser.uid}/clients`);
                        const clientsSnapshot = await getDocs(clientsCollectionRef);

                        let allPlannedData = [];

                        // Loop through each client document
                        for (const clientDoc of clientsSnapshot.docs) {
                            const clientId = clientDoc.id;

                            // Reference to the planned subcollection for the client
                            const plannedCollectionRef = collection(db, `Customers/${currentUser.uid}/clients/${clientId}/planned`);
                            const plannedSnapshot = await getDocs(plannedCollectionRef);

                            // Loop through each planned document
                            plannedSnapshot.forEach((plannedDoc) => {
                                allPlannedData.push({
                                    clientId,
                                    ...plannedDoc.data(),
                                    id: plannedDoc.id, // Add document reference
                                });
                            });
                        }

                        // If there's planned data, set it in the state and local storage
                        if (allPlannedData.length > 0) {
                            setOrdersPlanned(allPlannedData);
                            setFilteredOrdersPlanned(allPlannedData);
                            setStoredJSON(`plannedOrders_${currentUser.uid}`, allPlannedData, orderCacheOptions);
                        } else {
                            setOrdersPlanned([]);
                            setFilteredOrdersPlanned([]);
                            console.log('Planned Order document not found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching planned orders data:', error);
                }
            }
        };

        const fetchOrdersActive = async () => {
            if (currentUser) {
                try {
                    const storedActiveData = getStoredJSON(`activeOrders_${currentUser.uid}`);
                    if (storedActiveData) {
                        storedActiveData.sort((a, b) => new Date(b.submittedTime) - new Date(a.submittedTime));
                        setOrders(storedActiveData);
                        setFilteredOrders(storedActiveData);
                    } else {
                        const clientsCollectionRef = collection(db, `Customers/${currentUser.uid}/clients`);
                        const clientsSnapshot = await getDocs(clientsCollectionRef);
                        let allActiveData = [];

                        for (const clientDoc of clientsSnapshot.docs) {
                            const clientId = clientDoc.id;

                            const pendingCollectionRef = collection(db, `Customers/${currentUser.uid}/clients/${clientId}/active`);
                            const activeSnapshot = await getDocs(pendingCollectionRef);

                            activeSnapshot.forEach((pendingDoc) => {
                                allActiveData.push({
                                    clientId,
                                    id: pendingDoc.id, // Add the document ID
                                    ...pendingDoc.data(),
                                });
                            });
                        }


                        if (allActiveData.length > 0) {
                            allActiveData.sort((a, b) => new Date(b.submittedTime) - new Date(a.submittedTime));
                            setOrders(allActiveData);
                            setFilteredOrders(allActiveData);
                            setStoredJSON(`activeOrders_${currentUser.uid}`, allActiveData, orderCacheOptions);
                        } else {
                            setOrders([]);
                            setFilteredOrders([]);
                            console.log('Active Order document not found');
                        }

                    }
                } catch (error) {
                    console.error('Error fetching orders data:', error);
                }
            }
        };
        
        const fetchUserName = async () => {
            if (currentUser) {
                try {
                    const storedUserData = localStorage.getItem(`userData_${currentUser.uid}`);
                    if (storedUserData) {
                        const userData = JSON.parse(storedUserData);
                        setFname(userData.fname);
                    } else {
                        const userDoc = await db.collection('Users').doc(currentUser.uid).get();
                        if (userDoc.exists) {
                            const userData = userDoc.data();
                            setFname(userData.fname);
                            localStorage.setItem(`userData_${currentUser.uid}`, JSON.stringify(userData));
                        } else {
                            console.log('User document not found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            }
        };

        fetchUserName();

        fetchOrdersPending();
        fetchOrdersPlanned();
        fetchOrdersActive();
    }, [currentUser, orderCacheOptions]);



    //
    const [updating, setUpdating] = useState(false);

    const onUpdate = async () => {
        console.log("OnUpdate Running");
        
        setUpdating(true);
        const startTime = Date.now();

        try {
            const clientsCollectionRef = collection(db, `Customers/${currentUser.uid}/clients`);
            const clientsSnapshot = await getDocs(clientsCollectionRef);
            const fetchOrderType = async (type) => {
                const results = await mapWithConcurrency(clientsSnapshot.docs, 10, async (clientDoc) => {
                    const clientId = clientDoc.id;
                    const orderSnapshot = await getDocs(
                        collection(db, `Customers/${currentUser.uid}/clients/${clientId}/${type}`)
                    );

                    return orderSnapshot.docs.map((orderDoc) => ({
                        clientId,
                        id: orderDoc.id,
                        ...orderDoc.data(),
                    }));
                });

                return results.flat();
            };

            const [allPendingData, allPlannedData, allActiveData] = await Promise.all([
                fetchOrderType("pending"),
                fetchOrderType("planned"),
                fetchOrderType("active"),
            ]);

            if (allPendingData.length > 0) {
                setOrdersPending(allPendingData);
                setFilteredPendingOrders(allPendingData);
                setStoredJSON(`pendingOrders_${currentUser.uid}`, allPendingData, orderCacheOptions);
            } else {
                setOrdersPending([]);
                setFilteredPendingOrders([]);
                setStoredJSON(`pendingOrders_${currentUser.uid}`, [], orderCacheOptions);
                console.log('Pending Order document not found');
            }

            if (allPlannedData.length > 0) {
                setOrdersPlanned(allPlannedData);
                setFilteredOrdersPlanned(allPlannedData);
                setStoredJSON(`plannedOrders_${currentUser.uid}`, allPlannedData, orderCacheOptions);
            } else {
                setOrdersPlanned([]);
                setFilteredOrdersPlanned([]);
                setStoredJSON(`plannedOrders_${currentUser.uid}`, [], orderCacheOptions);
                console.log('Planned Order document not found');
            }

            if (allActiveData.length > 0) {
                allActiveData.sort((a, b) => new Date(b.submittedTime) - new Date(a.submittedTime));
                setOrders(allActiveData);
                setFilteredOrders(allActiveData);
                setStoredJSON(`activeOrders_${currentUser.uid}`, allActiveData, orderCacheOptions);
            } else {
                setOrders([]);
                setFilteredOrders([]);
                setStoredJSON(`activeOrders_${currentUser.uid}`, [], orderCacheOptions);
                console.log('Active Order document not found');
            }
        } catch (error) {
            console.error('Error updating orders:', error);
        }

        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) /1000;
        setUpdating(false);
        triggerNotification("ordersUpdated");
        
        if (alertsOn) {
            alert(`Orders Updated in ${timeElapsed} secs`);
        }
    };

    const onUpdatePlanned = async () => {
        setUpdating(true);
        const startTime = Date.now();

        // Planned
        try {
            const clientsCollectionRef = collection(db, `Customers/${currentUser.uid}/clients`);
            const clientsSnapshot = await getDocs(clientsCollectionRef);

            let allPlannedData = [];

            // Create an array of promises for fetching planned collections
            const plannedPromises = clientsSnapshot.docs.map(async (clientDoc) => {
                const clientId = clientDoc.id;

                // Reference to the planned subcollection for the client
                const plannedCollectionRef = collection(db, `Customers/${currentUser.uid}/clients/${clientId}/planned`);
                const plannedSnapshot = await getDocs(plannedCollectionRef);

                plannedSnapshot.forEach((plannedDoc) => {
                    allPlannedData.push({
                        clientId,
                        ...plannedDoc.data(),
                        id: plannedDoc.id, // Add document ID
                    });
                });
            });

            // Run all planned fetches in parallel
            await Promise.all(plannedPromises);

            if (allPlannedData.length > 0) {
                setOrdersPlanned(allPlannedData);
                setFilteredOrdersPlanned(allPlannedData);
                setStoredJSON(`plannedOrders_${currentUser.uid}`, allPlannedData, orderCacheOptions);
            } else {
                console.log('Planned Order document not found');
            }

        } catch (error) {
            console.error('Error fetching planned orders data:', error);
        }

        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) /1000;
        setUpdating(false);
        triggerNotification("ordersUpdated");
        
        if (alertsOn) {
            alert(`Orders Updated in ${timeElapsed} secs`);
        }
    };
    

    useEffect(() => {
        const checkOrdersUpdated = async () => {
            try {
                const userDocRef = doc(db, `Users/${currentUser.uid}`);
                const userSnap = await getDoc(userDocRef);
    
                if (userSnap.exists()) {
                    const userData = userSnap.data();
    
                    if (userData.ordersUpdated === true) {
                        onUpdate();
                        await updateDoc(userDocRef, {
                            ordersUpdated: false
                        });
                        console.log("ordersUpdated was true; onUpdate triggered and reset to false.");
                    }
                }
            } catch (error) {
                console.error("Error checking or updating ordersUpdated:", error);
            }
        };
    
        // Run if newOrder is true in localStorage
        const newOrder = localStorage.getItem('newOrder');
        if (newOrder === 'true') {
            onUpdate();
            localStorage.setItem('newOrder', 'false');
        }
    
        checkOrdersUpdated();
    
        // eslint-disable-next-line
    }, [orderForm]);
    
    const handlePendingSearch = (query) => {
        setSearchQuery(query);
        const filtered = ordersPending.filter((order) =>
            order.customerName.toLowerCase().includes(query.toLowerCase()) ||
            order.customerCode.toLowerCase().includes(query.toLowerCase()) ||
            
            order.products.some((prod) => prod.productCode.toLowerCase().includes(query.toLowerCase())) ||
            order.products.some((prod) => prod.name.toLowerCase().includes(query.toLowerCase()))
        );
        setFilteredPendingOrders(filtered);
    }

    const handleActiveSearch = (query) => {
        setSearchQuery(query);
        const filtered = orders.filter((order) =>
            order.customerName.toLowerCase().includes(query.toLowerCase()) ||
            order.customerCode.toLowerCase().includes(query.toLowerCase()) ||
            order.orderERP.toString().includes(query.toLowerCase()) ||
            
            order.products.some((prod) => prod.productCode.toLowerCase().includes(query.toLowerCase())) ||
            order.products.some((prod) => prod.name.toLowerCase().includes(query.toLowerCase()))
        );
        setFilteredOrders(filtered);
    }

    const handlePlannedSearch = (query) => {
        setSearchQuery(query);
        const filtered = ordersPlanned.filter((order) =>
            order.customerName.toLowerCase().includes(query.toLowerCase()) ||
            order.customerCode.toLowerCase().includes(query.toLowerCase()) ||
            
            order.products.some((prod) => prod.productCode.toLowerCase().includes(query.toLowerCase())) ||
            order.products.some((prod) => prod.name.toLowerCase().includes(query.toLowerCase()))
        );
        setFilteredOrdersPlanned(filtered);
    }
    

    const filterOrdersByIndustry = (industry) => {
        const filtered = orders.filter(customer => industry === '' || customer.customerIndustry === industry);
        setFilteredOrders(filtered);
    };

    const filterPendingOrdersByIndustry = (industry) => {
        const filtered = ordersPending.filter(customer => industry === '' || customer.customerIndustry === industry);
        setFilteredPendingOrders(filtered);
    };

    // const filterPlannedOrdersByIndustry = (industry) => {
    //     const filtered = ordersPlanned.filter(customer => industry === '' || customer.customerIndustry === industry);
    //     setFilteredOrdersPlanned(filtered);
    // };



    const filterOrdersByPrice = (price) => {
        const filtered = orders.filter(customer => price === '' || customer.priceType === price);
        setFilteredOrders(filtered);
    };

    const filterPendingOrdersByPrice = (price) => {
        const filtered = ordersPending.filter(customer => price === '' || customer.priceType === price);
        setFilteredPendingOrders(filtered);
    };

    const filterPlannedOrdersByPrice = (price) => {
        const filtered = ordersPlanned.filter(customer => price === '' || customer.priceType === price);
        setFilteredOrdersPlanned(filtered);
    };




    const filtering = (e) => {
        if(status === "A"){
            filterOrdersByIndustry(e.target.value);
        }
        if (status === "P"){
            filterPendingOrdersByIndustry(e.target.value);
        }
        if (status === "Planned"){
            filterPlannedOrdersByPrice(e.target.value);
        }
    }

    const filteringPrice = (e) => {
        if(status === "A"){
            filterOrdersByPrice(e.target.value);
        }
        if (status === "P"){
            filterPendingOrdersByPrice(e.target.value);
        }
        if (status === "Planned"){
            filterPlannedOrdersByPrice(e.target.value);
        }
    }


    const [count, setCount] = useState(0);
    const [custCount, setCustCount] = useState(0);

    useEffect(() => {

        if (status === "A"){
            setCount(filteredOrders.length);
            setCustCount(countUniqueCustomerCodes(filteredOrders))
        }
        if (status === "P"){
            setCount(filteredPendingOrders.length);
            setCustCount(countUniqueCustomerCodes(filteredPendingOrders))
        }
        if (status === "Planned"){
            setCount(filteredOrdersPlanned.length);
            setCustCount(countUniqueCustomerCodes(filteredOrdersPlanned))
        }

    }, [status, filteredOrders, filteredPendingOrders, filteredOrdersPlanned]);



    // Function to calculate the total grand total from pending orders
    const calculateTotalGrandTotal = (orders) => {
        return orders.reduce((total, order) => {
            return total + (parseFloat(order.grandTotal) || 0); // Add 0 for undefined grandTotal
        }, 0);
    };


    const totalGrandTotal = calculateTotalGrandTotal(ordersPending);
    const plannedGrandTotal = calculateTotalGrandTotal(ordersPlanned);
    const grandTotal = calculateTotalGrandTotal(orders);

    // Function to delete an order
    const handleDeleteOrder = async (clientId, orderId) => {
        // Check if clientId and orderId are not empty
        if (!clientId || !orderId) {
            console.warn("Client ID or Order ID is missing. Delete operation aborted.");
            return;
        }

        try {
            const orderRef = doc(db, `Customers/${currentUser.uid}/clients/${clientId}/planned`, orderId);
            await deleteDoc(orderRef);
            console.log(`Order with ID ${orderId} for client ${clientId} deleted successfully`);

            // Update the state to remove the deleted order
            setFilteredOrdersPlanned((prevOrders) =>
                prevOrders.filter((order) => !(order.clientId === clientId && order.id === orderId))
            );

            await onUpdatePlanned();
        } catch (error) {
            console.error("Error deleting order:", error);
        }
    };
    

    const downloadMapping = () => {
        if (status === "A") {
            downloadExcel(orders, "ActiveOrders.xlsx");
        }
        if (status === "P") {
            downloadExcel(ordersPending, "PendingOrders.xlsx");
        }
        if (status === "Planned") {
            downloadExcel(ordersPlanned, "PlannedOrders.xlsx");
        }
    };
    
    const statusMap = {
        A: {
            orders: filteredOrders,
            emptyTitle: "You Have No Active Orders",
            emptyDescription: "You Must Wait For the Plant to Update Your Order Status",
        },
        P: {
            orders: filteredPendingOrders,
            emptyTitle: "You Have No Pending Orders",
            emptyDescription: "You May Place Orders In the New Order Form",
        },
        Planned: {
            orders: filteredOrdersPlanned,
            emptyTitle: "You Have No Planned Orders",
            emptyDescription: "You May Place Orders In the New Order Form",
        },
        // All: {
        //     orders: [
        //         ...filteredOrders,
        //         ...filteredPendingOrders,
        //         ...filteredOrdersPlanned
        //     ],
        //     emptyTitle: "You Have No Orders",
        //     emptyDescription: "You Have No Active, Pending, or Planned Orders"
        // }
    };

    const currentStatus = statusMap[status] || {};
    
    
    
    const [selectedOrder, setSelectedOrder] = useState(null);
    
    // Function to handle both accountView and selected customer
    const handleViewOrder = (order) => {
        setOrderForm(true);
        setSelectedOrder(order);
    };

    const [listView, setListView] = useState(true);

    useEffect(() => {
      const storedPreference = localStorage.getItem('listViewPreference');
      if (storedPreference !== null) {
        setListView(JSON.parse(storedPreference));
      }
    }, []);
    
    // const [listView, setListView] = useState(() => {
    //     const storedSettings = localStorage.getItem('listViewPreference');
    //     return storedSettings ? JSON.parse(storedSettings).listView : true; // Default to false if null
    // });
    //
    
    
    
    
    
    
    const [secondaryNavigation, setSecondaryNavigation] = useState([
      // { name: 'Dashboard', href: '#', current: false },
      { name: 'Active Orders', href: 'A', current: true },
      { name: 'Pending Orders', href: 'P', current: false },
      { name: 'Planned Orders', href: 'Planned', current: false },
    ]);
    
    const LabelMapping = {
        A: "Active Orders",
        P: "Pending Orders",
        Planned: "Planned Orders",
    };
    
    const stats = [
      { name: `Number of ${LabelMapping[status]}`, value: `${count}`, unit: `${custCount} customers` },
      { name: 'Active Orders', stat: "A",  value: `$ ${numberWithCommas(grandTotal.toFixed(2))}`, unit: `${filteredOrders.length}` },
      { name: 'Pending Orders', stat: "P", value: `$ ${numberWithCommas(totalGrandTotal.toFixed(2))}`, unit: `${filteredPendingOrders.length}` },
      { name: 'Planned Orders', stat: "Planned", value: `$ ${numberWithCommas(plannedGrandTotal.toFixed(2))}`, unit: `${filteredOrdersPlanned.length}` },
    ]
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'A':
                return 'bg-green-600/25 text-green-600'; // Active
            case 'P':
                return 'bg-yellow-500/25 text-yellow-500'; // Pending
            case 'Planned':
                return 'bg-blue-600/25 text-blue-600'; // Planned
            default:
                return 'bg-gray-400/25 text-gray-400'; // Fallback
        }
    };
    
    const [collapse, setCollapse] = useState(false);
    
    const toggleCollapse = () =>{
        setCollapse(!collapse);
    }
  
    
    return (
        
        <>
            {orderForm ? (
                <NewOrder
                    order={selectedOrder}
                    isEditing={true}
                    close={() => setOrderForm(false)}
                />
            ) : (
                <div className={`animate-fadeIn flex h-[calc(100dvh-3.5rem)] w-full transition-all duration-500 ease-in-out`}>
                    
                    {updating && (
                        <Loading/>
                    )}
                    
                    {/*Redesign*/}
                    <div className={`z-20 ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} w-full`}>
                
                        <main>
                            
                            
                            <header>
                                
                                {/* Secondary navigation */}
                                <nav className={`flex items-center justify-between overflow-x-auto border-b ${darkMode ? 'border-neutral-700' : 'border-gray-300'} py-1.5`}>
                                    <div
                                        role="list"
                                        className="flex w-fit flex-none gap-x-6 px-4 text-sm/6 font-semibold text-gray-400 sm:px-6 lg:px-8"
                                    >
                                        {secondaryNavigation.map((item) => (
                                            <div
                                              key={item.name}
                                              onClick={() => {
                                                setStatus(item.href);
                                                setSecondaryNavigation((nav) =>
                                                  nav.map((navItem) => ({
                                                    ...navItem,
                                                    current: navItem.href === item.href,
                                                  }))
                                                );
                                              }}
                                              className="cursor-pointer"
                                            >
                                              <div className={item.current ? 'text-green-600 underline' : ''}>
                                                {item.name}
                                              </div>
                                            </div>
                                          ))}
                                    </div>
                                    
                                    <Button
                                        onClick={toggleCollapse}
                                        className={`z-50 mx-4`}
                                    >
                                        <ChevronDownIcon className={`${collapse ? 'rotate-180' : ''} transition-all duration-1000 size-6 text-green-600`}/>
                                    </Button>
                                    
                                </nav>
                                
                                {/* Heading */}
                                <div className={`${collapse ? 'hidden' : ''} flex flex-col items-start justify-between gap-x-8 ${darkMode ? ' bg-gray-700/10' : 'bg-neutral-300/50'} px-4 py-1.5 sm:flex-row sm:items-center`}>
                                    <div>
                                        <div className="flex items-center gap-x-3">
                                            <div className={`flex-none rounded-full ${getStatusColor(status)} p-1`}>
                                                <div className="size-2 rounded-full bg-current"/>
                                            </div>
                                            <h1 className="flex gap-x-3 text-base/7">
                                                <span className="font-semibold">Current Orders</span>
                                                <span className="font-semibold text-gray-500">{getQuarterString()}</span>
                                                <span className="text-gray-600">/</span>
                                                <span className="font-semibold">{LabelMapping[status]}</span>
                                            </h1>
                                        </div>
                                        <p className="text-xs/6 opacity-50 italic">Orders Placed Through Your Order Form</p>
                                    </div>
                                    <div className={`space-x-3 flex`}>
                                        
                                        <CustomButton
                                            color={"green"}
                                            onClick={onUpdate}
                                            icon={ArrowPathIcon}
                                            text={"Sync Orders"}
                                        />
                                        
                                        
                                        <Button onClick={downloadMapping} className="hidden flex items-center hover:scale-105 order-first flex-none rounded-full bg-green-600/10 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-green-600/30 ring-inset sm:order-none">
                                            <DocumentArrowDownIcon className={`size-4 text-green-600 mr-2`}/>
                                            Download Excel
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Stats */}
                                <div className={`${collapse ? 'hidden' : ''} grid grid-cols-1 ${darkMode ? ' bg-gray-700/10' : 'bg-neutral-300/50'} sm:grid-cols-2 lg:grid-cols-4`}>
                                    
                                    {stats.map((stat, statIdx) => {
                                        const isActive = stat.stat === status;
                                    
                                        // Define a map for border color classes
                                        const statusBorderColor = {
                                            A: 'border-green-600 bg-green-600/15',
                                            P: 'border-yellow-600 bg-yellow-600/15',
                                            Planned: 'border-blue-600 bg-blue-600/15',
                                        };
                                        
                                        const statColors = {
                                          A: 'bg-green-600/25  border-green-600 text-green-600',
                                          P: 'bg-yellow-600/25  border-yellow-600 text-yellow-600',
                                          Planned: 'bg-blue-600/25  border-blue-600 text-blue-600',
                                        };
                                    
                                        // Determine the border color to apply
                                        const activeBorder = isActive ? statusBorderColor[stat.stat] : (darkMode ? '' : '');
                                    
                                        return (
                                            <div
                                                key={stat.name}
                                                className={classNames(
                                                    statIdx % 2 === 1 ? 'sm:border-l' : statIdx === 2 ? 'lg:border-l' : '',
                                                    `border-t ${darkMode ? 'border-neutral-700' : 'border-gray-300'}`
                                                )}
                                            >
                                                <div className={`px-4 py-1 ${activeBorder ? `${activeBorder}` : ''}`}>
                                                    <p className="text-sm font-bold opacity-50 italic">{stat.name}</p>
                                                    <p className="-mt-1 flex items-baseline gap-x-2">
                                                        <span className="text-xl font-semibold tracking-tight">{stat.value}</span>
                                                        {stat.unit ? (
                                                            <span
                                                                className={`text-sm px-2 rounded-lg font-bold ${statColors[stat.stat] || 'text-green-600'}`}
                                                            >
                                                                {stat.unit}
                                                            </span>
                                                        ) : null}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                
                                
                                </div>
                            
                            </header>
                            
                            {/* Activity list */}
                            <div className="border-t border-white/10">
                                
                                
                                {/*Search bar row*/}
                                <div className={`grid grid-cols-8 w-full h-fit items-center ${darkMode ? 'text-white' : 'text-black'}`}>
                                
                                    {/*Search Bar*/}
                                    <div className="col-span-5 flex items-center h-fit text-sm">
                                        
                                        {/*<Button*/}
                                        {/*    onClick={() => {*/}
                                        {/*        const newListView = !listView;*/}
                                        {/*        setListView(newListView);*/}
                                        {/*        localStorage.setItem('listViewPreference', JSON.stringify(newListView));*/}
                                        {/*      }}*/}
                                        {/*    className="w-fit rounded-full mr-1.5 size-8 flex items-center justify-center align-middle">*/}
                                        {/*    {listView ? <RectangleGroupIcon className="size-5"/> : <QueueListIcon className="size-5"/>}*/}
                                        {/*</Button>*/}
                                        
                                        {/*<div className="text-sm mb-1">Customer</div>*/}
                
                                        <div className={`${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} w-full rounded-md flex items-center justify-center`}>
                                            <OrderSearchInput
                                                status={status}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                onSearch={
                                                    status === "A" ? handleActiveSearch :
                                                    status === "P" ? handlePendingSearch :
                                                    status === "Planned" ? handlePlannedSearch :
                                                    () => {}
                                                }
                                                darkMode={darkMode}
                                            />
                                        </div>
                
                                    </div>
                
                                    <span className="col-span-3 my-0.5 text-gray-500 isolate inline-flex justify-end rounded-md shadow-xs">
                                      <Select
                                          onChange={filtering}
                                          className={`relative gap-x-2 inline-flex items-center rounded-l-md bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                          <option value="">Select Industry...</option>
                                          <option value="BOTANICAL">Botanical Garden</option>
                                          <option value="DISTRIB">Product Distributor</option>
                                          <option value="EMPLOYEE">PFC-EMPLOYEE</option>
                                          <option value="FARM">Agriculture - Farm</option>
                                          <option value="GOLF">Golf Course</option>
                                          <option value="GOVERNMENT">Government Entity</option>
                                          <option value="INDUSTRIAL">Industrial</option>
                                          <option value="LAWNTREE">Lawn Care & Tree Care</option>
                                          <option value="MRSALE">One Time Purchase Expected</option>
                                          <option value="MUNICIPAL">Municipal/ School</option>
                                          <option value="Non-Profit">Non-Profit Account</option>
                                          <option value="OTHER">Other</option>
                                          <option value="SPORTFLD">Sportsfield</option>
                                      </Select>
                                      <Select
                                          onChange={filteringPrice}
                                          className={`relative gap-x-2 -ml-px inline-flex items-center bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                        <option value="">Select Price Type...</option>
                                        <option value="AG">Agriculture</option>
                                        <option value="DIST1">Distributor NE/INTL</option>
                                        <option value="DIST2">Distributor SE/CAN</option>
                                        <option value="NE">Northeast</option>
                                        <option value="SE">Southeast</option>
                                      </Select>
                                      <Button
                                          onClick={toggleDateAscDesc}
                                          className={`relative gap-x-2 -ml-px inline-flex items-center bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                        Placed on Date
                                        <ArrowUpIcon className={`size-4 ${DateAscDesc ? '' : 'rotate-180'} duration-500`}/>
                                      </Button>
                                      <Button
                                          onClick={toggleAscDesc}
                                          className={`relative gap-x-2 -ml-px inline-flex items-center rounded-r-md bg-gray-700 bg-opacity-10 ${darkMode ? 'ring-neutral-700' : 'ring-gray-300'} px-3 py-1 text-xs ring-1 ring-inset hover:bg-opacity-25 hover:text-green-600 hover:tracking-wider duration-500 focus:z-10`}
                                      >
                                        Order Total
                                        <ArrowUpIcon className={`size-4 ${AscDesc ? '' : 'rotate-180'} duration-500`}/>
                                      </Button>
                                    </span>
                                    
                                </div>
                                
                                
                                {/*Orders MAP*/}
                                <div className={`overflow-y-auto  ${collapse ? 'h-[calc(100dvh-8.6rem)]' : 'h-[calc(100dvh-16rem)]'} border-t scrollbar-hide ${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} p-1 rounded-b-lg scrollbar-hide`}>
                                    
                                    <OrderSection
                                        status={status}
                                        orders={currentStatus.orders || []}
                                        emptyTitle={currentStatus.emptyTitle}
                                        emptyDescription={currentStatus.emptyDescription}
                                        darkMode={darkMode}
                                        onUpdate={onUpdate}
                                        handleViewOrder={handleViewOrder}
                                        csrRep={fname}
                                        onDelete={(customerUID, id) => {
                                            if (status === "Planned") handleDeleteOrder(customerUID, id);
                                        }}
                                        listView={listView}
                                    />
    
                                </div>
        
                                
                                
                            </div>
                            
                        </main>
                    
                    </div>
                    
        
                </div>
            )}
        </>
    )
};


export default Orders;
